import React, { useState, useEffect } from 'react';
import {
  fetchFilteredCarbonAssets,
  formatNumber,
  formatDate,
  truncateText
} from '../../config/wilder_requests';
import AssetDetailsModal from './AssetDetailsModal';
import PurchaseRequestModal from './PurchaseRequestModal';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../locales';
import { buildSocialCarbonIssuanceUrl } from '../../utils/socialCarbonRegistry';
import {
  AssetsContainer,
  AssetsTitle,
  AssetsSubtitle,
  AssetsGrid,
  AssetCard,
  AssetHeader,
  AssetName,
  AssetType,
  AssetDetails,
  DetailItem,
  DetailLabel,
  DetailValue,
  AssetFooter,
  QuantityBadge,
  StandardBadge,
  LoadingSpinner,
  Spinner,
  ErrorMessage,
  NoResultsMessage,
  CardActions,
  CardButtonOutline,
  CardButtonSolid,
} from './CarbonAssets.styles';

const CarbonAssets = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para controlar o modal de detalhes
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAssetName, setSelectedAssetName] = useState('');
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [purchasePackageName, setPurchasePackageName] = useState('');

  useEffect(() => {
    fetchCarbonAssets();
  }, []);

  const fetchCarbonAssets = async () => {
    try {
      setLoading(true);
      const filteredAssets = await fetchFilteredCarbonAssets();
      setAssets(filteredAssets);
      setFilteredAssets(filteredAssets);
    } catch (err) {
      console.error('Erro ao buscar ativos:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Função para abrir o modal com Detalhes do Pacote de Créditos
  const openAssetModal = (assetName) => {
    setSelectedAssetName(assetName);
    setIsModalOpen(true);
  };

  // Função para fechar o modal
  const closeAssetModal = () => {
    setIsModalOpen(false);
    setSelectedAssetName('');
  };

  const openPurchaseModal = (name) => {
    setPurchasePackageName(name || '');
    setIsPurchaseModalOpen(true);
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
    setPurchasePackageName('');
  };



  if (loading) {
    return (
      <AssetsContainer>
        <LoadingSpinner>
          <Spinner />
        </LoadingSpinner>
      </AssetsContainer>
    );
  }

  if (error) {
    return (
      <AssetsContainer>
        <ErrorMessage>
          {t.carbonAssets.error}: {error}
        </ErrorMessage>
      </AssetsContainer>
    );
  }

  return (
    <AssetsContainer>
      <AssetsTitle>{t.carbonAssets.title}</AssetsTitle>
      <AssetsSubtitle>
        {t.carbonAssets.subtitle}
      </AssetsSubtitle>
      
      <AssetsGrid>
        {filteredAssets.map((asset, index) => {
          const socialCarbonUrl = buildSocialCarbonIssuanceUrl(asset._id);
          return (
          <AssetCard key={asset._id || index}>
            <AssetHeader>
              <AssetName 
                onClick={() => openAssetModal(asset['Asset Name'])}
                style={{ cursor: 'pointer' }}
                title={t.carbonAssets.clickDetails}
              >
                {truncateText(asset['Asset Name'] || t.carbonAssets.nameNotAvailable)}
              </AssetName>
              <AssetType>{asset['Asset type'] || t.carbonAssets.typeNotSpecified}</AssetType>
            </AssetHeader>
            
            <AssetDetails>
              <DetailItem>
                <DetailLabel>{t.carbonAssets.standard}</DetailLabel>
                <DetailValue>{asset.Standard || 'N/A'}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>{t.carbonAssets.vintage}</DetailLabel>
                <DetailValue>{asset.Vintage || 'N/A'}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>{t.carbonAssets.projectType}</DetailLabel>
                <DetailValue>{truncateText(asset['Project type'] || 'N/A', 30)}</DetailValue>
              </DetailItem>
              
              <DetailItem>
                <DetailLabel>{t.carbonAssets.createdDate}</DetailLabel>
                <DetailValue>{formatDate(asset['Created Date'])}</DetailValue>
              </DetailItem>
            </AssetDetails>
            
            <AssetFooter>
              <QuantityBadge>
                {formatNumber(asset["Quantity issued"] ?? asset.Quantity)}{" "}
                {t.carbonAssets.credits}
              </QuantityBadge>
              <StandardBadge>
                {asset.Standard || 'N/A'}
              </StandardBadge>
            </AssetFooter>

            <CardActions>
              {socialCarbonUrl ? (
                <CardButtonOutline
                  as="a"
                  href={socialCarbonUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.carbonAssets.wilderSocialCarbonDetails}
                </CardButtonOutline>
              ) : null}
              <CardButtonOutline
                type="button"
                onClick={() => openAssetModal(asset["Asset Name"])}
              >
                {t.carbonAssets.viewDetails}
              </CardButtonOutline>
              <CardButtonSolid
                type="button"
                onClick={() => openPurchaseModal(asset["Asset Name"])}
              >
                {t.carbonAssets.purchaseRequest}
              </CardButtonSolid>
            </CardActions>
          </AssetCard>
          );
        })}
      </AssetsGrid>

      {filteredAssets.length === 0 && (
        <NoResultsMessage>
          {t.carbonAssets.noAssets}
        </NoResultsMessage>
      )}

      {/* Modal de Detalhes do Pacote de Créditos */}
      <AssetDetailsModal
        isOpen={isModalOpen}
        onClose={closeAssetModal}
        assetName={selectedAssetName}
      />

      <PurchaseRequestModal
        isOpen={isPurchaseModalOpen}
        onClose={closePurchaseModal}
        packageName={purchasePackageName}
      />
    </AssetsContainer>
  );
};

export default CarbonAssets;
